import { useState, useEffect, useMemo } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";

function resolveTimezone(profileTz: string | null | undefined): string {
  if (!profileTz || profileTz === "auto" || profileTz === "UTC") {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return profileTz;
}

function getUtcOffset(tz: string, now: Date): string {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const parts = dtf.formatToParts(now);
    const tzName = parts.find((p) => p.type === "timeZoneName")?.value || "";
    // shortOffset returns like "GMT+3:30" or "GMT-5"
    const match = tzName.match(/GMT([+-]\d{1,2}(?::\d{2})?)/);
    if (match) return `UTC${match[1]}`;
    return "UTC+0";
  } catch {
    return "UTC";
  }
}

export const GlobalClock = () => {
  const { data: profile } = useProfile();
  const isMobile = useIsMobile();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const tz = useMemo(() => resolveTimezone(profile?.timezone), [profile?.timezone]);

  const localTime = now.toLocaleTimeString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const day3 = now.toLocaleDateString("en-US", { timeZone: tz, weekday: "short" });
  const month3 = now.toLocaleDateString("en-US", { timeZone: tz, month: "short" });
  const dayNum = now.toLocaleDateString("en-US", { timeZone: tz, day: "numeric" });

  const utcOffset = useMemo(() => getUtcOffset(tz, now), [tz, now]);
  // Short tz label (e.g. "Asia/Tehran" -> "Tehran")
  const tzLabel = tz.split("/").pop()?.replace(/_/g, " ") || tz;

  if (isMobile) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
        <span className="text-foreground font-medium">{localTime.slice(0, 5)}</span>
        <span className="text-muted-foreground/60 text-[10px]">{day3} {dayNum}</span>
      </div>
    );
  }

  return (
    <div className="h-16 px-3 rounded-md border border-border inline-flex flex-col justify-center leading-none font-mono w-fit bg-secondary">
      {/* Time — bold, prominent */}
      <span className="text-foreground tabular-nums tracking-tight text-base text-left font-extrabold">
        {localTime}
      </span>
      {/* Date — bold, near the same size */}
      <span className="font-semibold text-foreground tabular-nums mt-1 text-xs">
        {day3} · {month3} {dayNum}
      </span>
      {/* UTC offset · timezone label — small, muted */}
      <span className="text-muted-foreground tracking-wide mt-1 truncate text-xs font-semibold">
        {utcOffset} · {tzLabel}
      </span>
    </div>
  );
};
